import React from "react";
import { ChevronDownIcon } from "lucide-react";
import { Localizer } from "@/main/react/Types";

type DiscussionPageSkeletonProps = {
    localizer: Localizer;
    section?: string;
};

// Mirrors the real rail's accordion panel (DiscussionRailAccordionItem) in its default open state,
// so the placeholder reserves the same chrome and the rail does not reflow — nor flash the
// closed-state accent tint — when it hydrates.
const RAIL_MODULE_CARD_CLASS =
    "overflow-hidden rounded-[1.5rem] border border-border/60 bg-card shadow-[var(--surface-shadow-soft)]";
// Same icon tile as the real accordion trigger (MODULE_ICON_TILE_CLASS in DiscussionRail).
const RAIL_ICON_TILE_CLASS =
    "mt-0.5 size-9 shrink-0 animate-pulse rounded-full border border-border/50 bg-muted/70";

function RailModuleSkeleton(props: { rows: number; withAvatar?: boolean }) {
    const { rows, withAvatar = true } = props;
    return (
        // data-home-reveal-open="true" (open and closed panels share one flat edge since 2026-09-23),
        // matching the rail which mounts with every module expanded.
        <div className={RAIL_MODULE_CARD_CLASS} data-home-reveal-effect="panel" data-home-reveal-open="true">
            {/* Accordion trigger header: icon tile + title/subtitle, with the chevron on the right. */}
            <div className="flex items-start justify-between gap-4 px-4 py-4">
                <div className="flex min-w-0 items-start gap-3">
                    <div className={RAIL_ICON_TILE_CLASS} />
                    <div className="min-w-0 flex-1 space-y-2 py-0.5">
                        <div className="h-3.5 w-24 animate-pulse rounded bg-muted" />
                        <div className="h-3 w-32 animate-pulse rounded bg-muted" />
                    </div>
                </div>
                <ChevronDownIcon className="size-4 shrink-0 translate-y-0.5 rotate-180 text-muted-foreground" />
            </div>
            {/* Accordion content: placeholder rows matching RailSkeletonRows in DiscussionRail. */}
            <div className="px-4 pb-4">
                <div className="space-y-3" aria-hidden="true">
                    {Array.from({ length: rows }).map((_, index) => (
                        <div key={index} className="rounded-2xl border border-border/60 bg-muted/40 p-3">
                            <div className="flex items-start gap-3">
                                {withAvatar ? <div className="size-12 shrink-0 animate-pulse rounded-full bg-accent" /> : null}
                                <div className="min-w-0 flex-1 space-y-2">
                                    <div className="h-3.5 w-3/4 animate-pulse rounded bg-accent" />
                                    <div className="h-3 w-1/2 animate-pulse rounded bg-accent" />
                                    <div className="h-3 w-2/3 animate-pulse rounded bg-accent" />
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

// Desktop-only placeholder for DiscussionRail (hidden below lg, just like the real rail). Matches
// the rail's block container (space-y-4 px-6 pb-6) and renders the three modules — Related (no
// avatar), Groups, and Series — which are all visible (loading) while the rail hydrates.
function DiscussionRailSkeleton() {
    return (
        <div className="space-y-4 px-6 pb-6" aria-hidden="true">
            <RailModuleSkeleton rows={4} withAvatar={false} />
            <RailModuleSkeleton rows={3} />
            <RailModuleSkeleton rows={2} />
        </div>
    );
}

export default function DiscussionPageSkeleton(props: DiscussionPageSkeletonProps) {
    const { localizer, section } = props;
    // `FQ-R6`. "opening" resolves `discussion.section.conversation`, NOT
    // `discussion.section.opening`, and that is deliberate rather than a copy/paste: the live strip
    // (`DiscussionPageSectionControls`, the `DISCUSSION-64` branch) labels the same state that way
    // because "Opening" is the storage term (`openingArgumentPublicId`) and describes the post's
    // opening text, not the focused thread the panel below actually contains. While this file
    // disagreed, the section visibly RENAMED itself from "Opening" to "Conversation" the moment the
    // island mounted — worst on the slow connections that are the only ones to see the skeleton.
    // Parity is pinned by `DiscussionPageSkeletonSectionLabel.spec.tsx` against
    // `buildDiscussionSectionDefs`, so a later relabel of either side fails rather than drifting.
    const sectionLabel = section === "conversation" || section === "opening"
        ? localizer("discussion.section.conversation")
        : localizer("discussion.section.conversations");

    return (
        <div className="w-full bg-background pb-6" data-playwright-id="discussion-page-skeleton">
            <div className="mx-auto w-full max-w-7xl px-0 lg:px-6">
                <div className="grid w-full gap-2 lg:grid-cols-[minmax(0,1fr)_28rem]">
                    <main className="min-w-0">
                        <div className="mx-auto mb-5 w-full max-w-250 pb-2">
                            {/* Twin of `DiscussionContentDisplayer`'s body card — same radius, same
                                hairline, and flat like it. Kept honest by
                                `components/ui/flatXRestingSurfaces.contract.test.tsx`. */}
                            <div className="w-full rounded-2xl border border-border bg-card p-4 sm:p-5 max-sm:rounded-none max-sm:border-0">
                                <div className="mx-auto w-full max-w-[78ch] animate-pulse space-y-4">
                                    <div className="h-3 w-32 rounded-full bg-muted" />
                                    <div className="h-9 w-4/5 rounded-full bg-muted" />
                                    <div className="h-5 w-52 rounded-full bg-muted" />
                                    <div className="space-y-2 pt-2">
                                        <div className="h-4 w-full rounded-full bg-muted" />
                                        <div className="h-4 w-full rounded-full bg-muted" />
                                        <div className="h-4 w-3/4 rounded-full bg-muted" />
                                    </div>
                                    <div className="flex flex-wrap gap-2 pt-2">
                                        <div className="h-10 w-40 rounded-full bg-muted" />
                                        <div className="h-10 w-30 rounded-full bg-muted" />
                                        <div className="h-10 w-30 rounded-full bg-muted" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mx-auto flex w-full min-w-0 max-w-200 flex-col items-stretch gap-3 max-sm:px-4">
                            <div
                                className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground"
                                data-playwright-id="discussion-page-skeleton-section-label"
                            >
                                {sectionLabel}
                            </div>
                            {/* Twin of the thought row's chat BUBBLE (ThoughtDisplayerInConversation's
                                `bubbleClass`), not of the row's root — the root is a transparent
                                `rounded-[1.75rem] p-2` wrapper, so a literal pairing would make these
                                placeholders invisible. Same measure in all three copies of this list
                                (here, PageNavigationTransition, discussion.jte), pinned by
                                `components/ui/flatXRestingSurfaces.contract.test.tsx`. */}
                            <div className="animate-pulse space-y-3">
                                <div className="h-18 rounded-[1.5rem] border border-border/60 bg-[color-mix(in_srgb,var(--card)_90%,var(--background))]" />
                                <div className="h-30 rounded-[1.5rem] border border-border/60 bg-[color-mix(in_srgb,var(--card)_90%,var(--background))]" />
                                <div className="h-30 rounded-[1.5rem] border border-border/60 bg-[color-mix(in_srgb,var(--card)_90%,var(--background))]" />
                            </div>
                        </div>
                    </main>

                    <div className="hidden min-w-0 lg:block">
                        <DiscussionRailSkeleton />
                    </div>
                </div>
            </div>
        </div>
    );
}
